import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/site/MaskedLines";
import { EditableText } from "@/components/cms/EditableText";
import { BlockArea } from "@/components/cms/BlockArea";

const label = "font-mono-label text-xs uppercase tracking-[0.25em] text-[#57534E]";
const WHAT = [
  ["about_what_1_title", "[Service one]", "about_what_1_text", "[A sentence or two about this part of what Cresway Media does.]"],
  ["about_what_2_title", "[Service two]", "about_what_2_text", "[A sentence or two about this part of what Cresway Media does.]"],
  ["about_what_3_title", "[Service three]", "about_what_3_text", "[A sentence or two about this part of what Cresway Media does.]"],
];

const Story = () => (
  <section className="mx-auto grid max-w-7xl gap-8 border-t-4 border-[#0A0A0A] px-5 py-14 sm:px-8 md:grid-cols-12 lg:px-12 lg:py-20" data-testid="about-story">
    <Reveal className="md:col-span-4"><p className={label}>01 · Our story</p>
      <EditableText k="about_story_title" as="h2" className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-[-0.03em] sm:text-4xl" text="[How it started]" testId="about-story-title" />
    </Reveal>
    <Reveal className="md:col-span-7 md:col-start-6" delay={0.1}>
      <EditableText k="about_story_text" as="p" className="text-base leading-relaxed text-[#3a3836] sm:text-lg" testId="about-story-text"
        text={"[Tell visitors how Cresway Media began, why you started it and what you're building towards.]\n\n[Add a second paragraph if you'd like, for example what makes Cresway Media different.]"} />
    </Reveal>
  </section>
);

const WhatWeDo = () => (
  <section className="bg-[#0A0A0A] text-white" data-testid="about-what">
    <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:px-12 lg:py-20">
      <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-zinc-300">02 · What we do</p>
      <EditableText k="about_what_title" as="h2" className="mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight tracking-[-0.03em] sm:text-4xl" text="[What Cresway Media does, in one line]" testId="about-what-title" />
      <ol className="mt-10 grid gap-px bg-white/15 md:grid-cols-3">
        {WHAT.map(([tk, tt, dk, dt], i) => (
          <li key={tk} className="bg-[#0A0A0A] p-6 md:pr-8" data-testid={`about-what-${i + 1}`}>
            <span className="font-mono text-xs font-bold text-[#E62314]">0{i + 1}</span>
            <EditableText k={tk} as="p" className="mt-3 font-display text-xl font-extrabold" text={tt} testId={`about-what-${i + 1}-title`} />
            <EditableText k={dk} as="p" className="mt-2 text-sm leading-relaxed text-zinc-300" text={dt} testId={`about-what-${i + 1}-text`} />
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const GetInTouch = () => (
  <section className="mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:px-12 lg:py-20" data-testid="about-contact">
    <p className={label}>03 · Get in touch</p>
    <div className="mt-3 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <EditableText k="about_contact_text" as="p" className="max-w-2xl font-display text-3xl font-extrabold leading-tight tracking-[-0.03em] sm:text-4xl" text="[An invitation to get in touch, e.g. what people can contact you about.]" testId="about-contact-text" />
      <Link to="/contact" className="group flex shrink-0 items-center justify-center gap-2 bg-[#0A0A0A] px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-[#E62314]" data-testid="about-contact-btn">
        Contact us<ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="grain mx-auto max-w-7xl px-5 pb-14 pt-12 sm:px-8 sm:pt-20 lg:px-12">
        <p className={label}><span className="mr-3 inline-block h-2 w-2 rounded-full bg-[#E62314] align-middle" />About us</p>
        <EditableText k="about_headline" as="h1" className="mt-6 max-w-5xl font-display text-4xl font-extrabold leading-[0.95] tracking-[-0.04em] sm:text-5xl lg:text-6xl" text="[A short headline about Cresway Media]" testId="about-headline" />
        <EditableText k="about_intro" as="p" className="mt-6 max-w-2xl text-base text-[#3a3836] sm:text-lg" text="[One or two sentences introducing Cresway Media to someone who has never heard of it.]" testId="about-intro" />
      </section>
      <Story />
      <WhatWeDo />
      <GetInTouch />
      <BlockArea slug="about" className="pb-24" />
    </div>
  );
}
